/* eslint-disable no-console */
import { storage } from '@abyss/web/tools/storage';
import { Alert } from '@abyss/web/ui/Alert';
import find from 'lodash/find';
import React, { useEffect, useState } from 'react';

import { MaximumProviderLimitBanner } from '../../../common/BookmarkButton/MaximumProviderLimitBanner';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import {
  AlertStyling,
  LimitedProviderBannerStyling,
} from '../../../common/DetailsPageHeader/DetailsPageHeader.styled';
import { BreakdownDrawer } from '../../../common/SmartChoiceBreakDownDrawer/BreakdownDrawer';
import { getFeatureFlag } from '../../../common/Utils';
import { Footer } from '../../../frontends/ProviderSearch/routes/Footer';
import { useGetRecommendationPreferencesStatus } from '../../../hooks/useGetRecommendationPreferencesStatus';
import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useLagoon } from '../../../hooks/useLagoon';
import { useProviderDetailsWidget } from '../../../hooks/useProviderDetailsWidget';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useWidgetSmartChoice } from '../../../hooks/useWidgetSmartChoice';
import { ProviderDetails, ProviderType } from '../../../models/ProviderDetails';
import { useCareTeamStore } from '../../../store/useCareTeamStore';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { shouldSuppressVirtualOnlyLocationIndicator } from '../../../utils/indicator.utils';
import {
  UserDDPCode,
  getNetworkStatus,
  getSelectedLocationIndex,
} from '../../../utils/providerDetails.utils';
import {
  formatProviderNameWithFirstMiddleLastName,
  getNameAttrs,
} from '../../../utils/providerNames.utils';
import { getCurrentMember } from '../../../utils/user.utils';
import { CardWrapper, CardWrapperDetailsPage } from '../../CardWrapper';
import { Constants, ProviderTabs, SHOULD_GET_HG_DATA } from '../../Constants';
import { DisclaimersContainer } from '../../DisclaimersContainer/DisclaimersContainer';
import { SmartChoiceBreakDownL1 } from '../../SmartChoice/SmartChoiceBreakDownL1';
import { DetailsWidgetHeader } from './DetailsWidgetHeader';
import { DetailsWidgetNoDetails } from './DetailsWidgetNoDetails';
import { DetailsWidgetSkeleton } from './DetailsWidgetSkeleton';
import { DetailsWidgetTabs } from './DetailsWidgetTabs';
import { WidgetDisclaimersWrapper } from './DetailsWidgetView.styled';
import { ErrorHandlingWidget } from './ErrorHandlingWidget';
import { About } from './WidgetPages/About';
import { Locations } from './WidgetPages/Locations';
import { Quality } from './WidgetPages/Quality';

type Props = {
  claimSystemTypeCode?: string;
  coverageType: string;
  ddpUserCode: UserDDPCode;
  eligibilitySystemTypeCode?: string;
  latitude: string;
  lob: string;
  locationId: string;
  longitude: string;
  outOfPocketCost?: string;
  planVariationCode?: string;
  planYear: string;
  policyId: string;
  providerId: string;
  providerType: ProviderType;
  reciprocityId: string;
  totalCost?: string;
  membershipCategory?: string;
  population?: string;
  isSmartChoiceEnabled: boolean;
};

export const DetailsWidgetView = ({
  claimSystemTypeCode,
  coverageType,
  ddpUserCode,
  eligibilitySystemTypeCode,
  latitude,
  lob,
  locationId,
  longitude,
  outOfPocketCost,
  planVariationCode,
  planYear,
  policyId,
  providerId,
  providerType,
  reciprocityId,
  totalCost,
  membershipCategory,
  population,
  isSmartChoiceEnabled,
}: Props) => {
  const hsidUuid = useHsidUuid();
  const isWidgetSmartChoiceEnabled = useWidgetSmartChoice(
    isSmartChoiceEnabled,
    coverageType
  );
  const defaultActiveTab =
    providerType === ProviderType.ORGANIZATION
      ? ProviderTabs.LOCATIONS
      : ProviderTabs.ABOUT;
  const [activeTab, setActiveTab] = useState<ProviderTabs>(defaultActiveTab);
  const [retry, toggleRetry] = useState<boolean>(false);
  const [coachmarkWeb, setCoachmarkWebOpen] = useState<boolean>(false);

  const isGEHAV2 = shouldUseGehaV2Styling();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const lagoonData = useLagoon('ui-messaging')();

  const suppressionLagoonData = useLagoon('feature-supression')();
  const setUIFeatureSuppressionState = useStore(
    StoreKeys.SET_UI_FEATURE_SUPPRESSION
  );
  const showSmartChoiceLevel1Breakdown = useStore(
    StoreKeys.SHOW_SMART_CHOICE_LEVEL1_BREAKDOWN
  );
  const setShowSmartChoiceLevel1Breakdown = useStore(
    StoreKeys.SET_SHOW_SMART_CHOICE_LEVEL1_BREAKDOWN
  );

  const showSmartChoiceLevel3Breakdown = useStore(
    StoreKeys.SHOW_SMART_CHOICE_LEVEL3_BREAKDOWN
  );
  const setShowSmartChoiceLevel3Breakdown = useStore(
    StoreKeys.SET_SHOW_SMART_CHOICE_LEVEL3_BREAKDOWN
  );
  const drawerType = useStore(StoreKeys.DRAWER_TYPE);
  const setDrawerTypes = useStore(StoreKeys.SET_DRAWER_TYPE);
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );

  const { showMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    showMaximumProviderLimitBanner: state.showMaximumProviderLimitBanner,
  }));
  const shouldGetHGData = SHOULD_GET_HG_DATA;
  const currentMember = getCurrentMember();
  {
    /* Assume hasBH coverage is always true in the widget to pass the virtualOnlyLocationIndFlag to PES. This is a tactical decision approved by Julie Chaloner, as documented in the attached conversation screenshots under the US8546063 discussion tab.
  const memberCoverages = getCoverageTypes(currentMember);
  const hasBH =
    memberHasBHCoverage(memberCoverages) ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']; */
  }
  const enableRRVirtualCareCEXWidget = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RR_VIRTUAL_CARE_CEX_WIDGET
  );
  const featureSuppressionData = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const virtualOnlyLocationIndFlagKey =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_ONLY_LOCATION_INDICATOR_UNET_RAILROAD;
  const virtualOnlyLocationInd =
    enableRRVirtualCareCEXWidget &&
    shouldSuppressVirtualOnlyLocationIndicator(
      currentMember?.policyNumber,
      true, // "Set hasBH to true as part of the tactical approach approved by Julie Chaloner"
      virtualOnlyLocationIndFlagKey,
      featureSuppressionData
    );

  const {
    data: providerData,
    error,
    isLoading,
  } = useProviderDetailsWidget({
    claimSystemTypeCode,
    coverageType,
    eligibilitySystemTypeCode,
    latitude,
    lob,
    locationId,
    longitude,
    planVariationCode,
    planYear,
    policyId,
    providerId,
    providerType,
    reciprocityId,
    shouldGetHGData,
    membershipCategory,
    population,
    virtualOnlyLocationInd,
    isWidgetSmartChoiceEnabled,
  });
  const handleRetry = () => toggleRetry(!retry);
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;
  const [preferencesData, getPreferences] =
    useGetRecommendationPreferencesStatus({
      handleSuccess: (data) => {
        const preferences = data?.data?.recommendationPreferencesStatus;
        setRecommendationPreferences(preferences);
      },
    });

  useEffect(() => {
    setUIFeatureSuppressionState({
      suppressionLagoonData,
      memberPolicy: policyId,
    });
  }, []);

  useEffect(() => {
    console.log({ isWidgetSmartChoiceEnabled }, 'before fetching preferences');
    if (!isWidgetSmartChoiceEnabled) return;
    console.log({ isWidgetSmartChoiceEnabled }, 'fetching preferences');
    getPreferences({
      variables: { ...(hsidUuid && { userId: hsidUuid }), isSuperUser },
    });
  }, [isWidgetSmartChoiceEnabled]);

  const providerDetails =
    providerData.facilityDetails?.providerDetails ||
    providerData?.providerDetails?.providerDetails;

  if (isLoading) {
    return <DetailsWidgetSkeleton />;
  }

  if (!providerDetails) {
    return <DetailsWidgetNoDetails />;
  }

  if (error) {
    return <ErrorHandlingWidget handleRetry={handleRetry} />;
  }

  const {
    areaOfExpertise,
    biographicData,
    boardCertifications,
    culturalCompetencies,
    educationInfo,
    ethnicity,
    gender,
    hospitalAffiliations,
    imageLocation,
    healthGradeRating,
    languagesSpoken,
    licenseNumber,
    npi,
    organizationType = [],
    primaryDegrees,
    providerLocations = [],
    providerName,
    specialties,
    surveyCount,
    virtualCareOffered,
    organizationCode = [],
    licensedStates,
  } = providerDetails;

  const selectedIndex = getSelectedLocationIndex(providerLocations, locationId);
  const selectedLocation = providerLocations?.[selectedIndex] || {};

  const {
    address,
    ddpCode: ddpFacilityCode,
    facilityProviderSpecialties = {},
    network,
  } = selectedLocation;
  const { isPreferredLabNetwork, isFreeStandingFacility } =
    facilityProviderSpecialties;
  const networkStatus = getNetworkStatus(network?.status);
  const nameAttrs = getNameAttrs(providerDetails);

  const limitedAccessBanner = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.LIMITED_ACCESS_TO_PROVIDER_BANNER,
  });
  const isLimitedAccessToProvider: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.LIMITED_ACCESS_TO_PROVIDER_BANNER_AND_SCHEDULE
  );
  const isStateLicenseSectionEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_STATE_LICENSE_SECTION
  );

  const getActiveTabContent = () => {
    switch (activeTab) {
      case ProviderTabs.ABOUT:
        return (
          <About
            areaOfExpertise={areaOfExpertise}
            biographicData={biographicData}
            culturalCompetencies={culturalCompetencies}
            educationInfo={educationInfo}
            ethnicity={ethnicity}
            gender={gender}
            hospitalAffiliations={hospitalAffiliations}
            isSmartChoiceEnabled={isWidgetSmartChoiceEnabled}
            isStateLicenseSectionEnabled={isStateLicenseSectionEnabled}
            languagesSpoken={languagesSpoken}
            licenseNumber={licenseNumber}
            licensedStates={licensedStates}
            npi={npi}
            preferencesData={
              preferencesData?.data?.recommendationPreferencesStatus
            }
            primaryDegree={primaryDegrees?.[0]}
            providerDetails={providerDetails}
            providerName={formatProviderNameWithFirstMiddleLastName(
              providerDetails
            )}
            selectedLocation={selectedLocation}
            setActiveTab={(tab: ProviderTabs) => setActiveTab(tab)}
            specialties={specialties}
            userState={address?.state}
          />
        );
      case ProviderTabs.LOCATIONS:
        return (
          <Locations
            latitude={latitude}
            locationId={locationId}
            longitude={longitude}
            providerDetails={providerDetails}
          />
        );
      case ProviderTabs.QUALITY:
        return (
          <Quality
            boardCertifications={boardCertifications}
            organizationCode={organizationCode}
            providerName={providerName}
            providerType={providerType}
            selectedLocation={selectedLocation}
          />
        );
      default:
        return <React.Fragment />;
    }
  };

  if (showSmartChoiceLevel1Breakdown) {
    return (
      <SmartChoiceBreakDownL1
        coachmarkWeb={coachmarkWeb}
        handleCloseDrawer={() => setShowSmartChoiceLevel1Breakdown(false)}
        isOpen={false}
        isWidget={showSmartChoiceLevel1Breakdown}
        onBreakDownClick={(key: string) => {
          setShowSmartChoiceLevel1Breakdown(false);
          setDrawerTypes([key, 'L1']);
          setShowSmartChoiceLevel3Breakdown(true);
        }}
        onClickSmartChoiceHandler={() => {
          // Handle smart choice click if needed
        }}
        onCloseCoachmarkWeb={() => setCoachmarkWebOpen(false)}
        preferencesData={preferencesData?.data?.recommendationPreferencesStatus}
        providerDetails={providerDetails}
        recommendationDetails={selectedLocation?.recommendationDetails}
        setCoachmarkWebOpen={setCoachmarkWebOpen}
      />
    );
  }

  if (showSmartChoiceLevel3Breakdown) {
    return (
      <BreakdownDrawer
        drawerType={drawerType[0]}
        handleTabChange={() => {}}
        isOpen={showSmartChoiceLevel3Breakdown}
        isWidget={isWidget}
        locationKey={selectedLocation?.locationKey}
        preferencesData={preferencesData?.data?.recommendationPreferencesStatus}
        providerDetails={providerDetails as ProviderDetails}
        recommendationDetails={selectedLocation?.recommendationDetails}
        setIsOpen={() => {
          setShowSmartChoiceLevel3Breakdown(false);
          const type = drawerType[1];
          if (type === 'L1') {
            setShowSmartChoiceLevel1Breakdown(true);
          }
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <CardWrapperDetailsPage>
        <DetailsWidgetHeader
          coverageType={coverageType}
          ddpFacilityCode={ddpFacilityCode}
          ddpUserCode={ddpUserCode}
          imageLocation={imageLocation}
          inNetwork={networkStatus}
          isFreeStandingFacility={isFreeStandingFacility}
          isOxPreferredLab={selectedLocation?.isOxPreferredLab}
          isPreferredLabNetwork={isPreferredLabNetwork}
          nameAttrs={nameAttrs}
          organizationType={organizationType?.[0]}
          outOfPocketCost={outOfPocketCost}
          primarySpeciality={specialties?.[0]}
          professionalDesignation={primaryDegrees?.[0]}
          providerName={providerName}
          providerType={providerType}
          rating={healthGradeRating}
          surveyCount={surveyCount}
          totalCost={totalCost}
          virtualCareOffered={virtualCareOffered}
        />
      </CardWrapperDetailsPage>
      {isLimitedAccessToProvider &&
        selectedLocation?.hospitalBasedProviderIndicator && (
          <Alert
            css={{ ...AlertStyling, ...LimitedProviderBannerStyling }}
            data-testid="limited-access-to-provider-banner"
            title={limitedAccessBanner?.title}
            variant="info"
          >
            {limitedAccessBanner?.message}
          </Alert>
        )}
      {showMaximumProviderLimitBanner && (
        <MaximumProviderLimitBanner isCareTeamView={false} />
      )}
      <CardWrapperDetailsPage>
        <DetailsWidgetTabs
          providerType={providerType}
          setActiveTab={setActiveTab}
        />
        {getActiveTabContent()}
      </CardWrapperDetailsPage>
      <WidgetDisclaimersWrapper>
        <DisclaimersContainer
          isViewAllDisclaimer
          isVisionCare={coverageType === 'V'}
          showPremiumCareDisclaimer={coverageType !== 'B'}
        />
      </WidgetDisclaimersWrapper>
      <CardWrapper
        backgroundColor={isGEHAV2 ? '#FAFAFA' : '$infoFooterBackground'}
        isWidget
      >
        <Footer />
      </CardWrapper>
    </React.Fragment>
  );
};
